import React, { useState } from "react";
import { DatePicker, Input, message, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  ClientApartment,
  BlockedPeriod,
  updateClientApartment,
} from "../../../utils/api/apartmentRentalsAPI";
import { BASE_API_URL } from "../../../utils/consts";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface Props {
  apartments: ClientApartment[];
  onApartmentsChange: (updated: ClientApartment) => void;
}

const PRESETS: { label: string; days: number | null; months?: number }[] = [
  { label: "1 dan", days: 0 },
  { label: "5 dana", days: 4 },
  { label: "10 dana", days: 9 },
  { label: "1 mjesec", days: null, months: 1 },
  { label: "2 mjeseca", days: null, months: 2 },
];

function applyPreset(from: Dayjs, preset: typeof PRESETS[0]): Dayjs {
  if (preset.months) return from.add(preset.months, "month").subtract(1, "day");
  return from.add(preset.days!, "day");
}

const BlockedDatesTab: React.FC<Props> = ({ apartments, onApartmentsChange }) => {
  const [selectedAptId, setSelectedAptId] = useState<string>(apartments[0]?._id ?? "");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const apt = apartments.find((a) => a._id === selectedAptId);

  const handlePreset = (preset: typeof PRESETS[0]) => {
    const from = dayjs().startOf("day");
    const to = applyPreset(from, preset);
    setDateRange([from, to]);
  };

  const handleAdd = async () => {
    if (!apt || !dateRange[0] || !dateRange[1]) {
      message.warning("Odaberite apartman i period");
      return;
    }
    const from = dateRange[0].format("YYYY-MM-DD");
    const to = dateRange[1].format("YYYY-MM-DD");
    if (from > to) {
      message.warning("Datum kraja mora biti nakon datuma početka");
      return;
    }
    setSaving(true);
    try {
      const newPeriod: BlockedPeriod = { from, to, reason: reason.trim() || undefined };
      const existing = apt.blockedPeriods || [];
      const updated = await updateClientApartment(apt._id, {
        blockedPeriods: [...existing, newPeriod],
      });
      onApartmentsChange(updated.data);
      setDateRange([null, null]);
      setReason("");
      message.success("Period blokiran");
    } catch {
      message.error("Greška pri čuvanju");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (periodId: string) => {
    if (!apt) return;
    setSaving(true);
    try {
      const remaining = (apt.blockedPeriods || []).filter((p) => p._id !== periodId);
      const updated = await updateClientApartment(apt._id, { blockedPeriods: remaining });
      onApartmentsChange(updated.data);
      message.success("Blokada uklonjena");
    } catch {
      message.error("Greška pri brisanju");
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (d: string) => dayjs(d).format("DD.MM.YYYY");
  const nightCount = (from: string, to: string) => dayjs(to).diff(dayjs(from), "day") + 1;

  const icalUrl = apt
    ? `${BASE_API_URL}/public/calendar/${apt._id}.ics`
    : null;

  const copyIcalUrl = () => {
    if (!icalUrl) return;
    navigator.clipboard.writeText(icalUrl).then(() => message.success("URL kopiran u clipboard"));
  };

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Apartment picker */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#7c6fcd", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
          Apartman
        </div>
        <Select
          value={selectedAptId}
          onChange={setSelectedAptId}
          style={{ width: "100%" }}
          placeholder="Odaberite apartman"
        >
          {apartments.map((a) => (
            <Option key={a._id} value={a._id}>{a.name}</Option>
          ))}
        </Select>
      </div>

      {/* iCal sync card */}
      {icalUrl && (
        <div style={{ background: "#f0f9ff", border: "1.5px solid #bae6fd", borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 18 }}>📅</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0c4a6e" }}>Booking.com sinhronizacija (iCal)</span>
          </div>
          <div style={{ fontSize: 12, color: "#0369a1", lineHeight: 1.6, marginBottom: 12 }}>
            Kopiraj URL i zalijepi ga u <strong>Booking.com Extranet → Calendar → Sync calendars → Add iCal link</strong>. Booking.com će automatski povlačiti rezervacije i blokade iz ovog sistema.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{
              flex: 1, padding: "7px 10px", background: "#fff", border: "1px solid #bae6fd",
              borderRadius: 7, fontSize: 11, color: "#0369a1", fontFamily: "monospace",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {icalUrl}
            </div>
            <button
              type="button"
              onClick={copyIcalUrl}
              style={{
                padding: "7px 14px", borderRadius: 7, fontSize: 12, fontWeight: 700,
                background: "#0ea5e9", color: "#fff", border: "none", cursor: "pointer",
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              Kopiraj URL
            </button>
          </div>
        </div>
      )}

      {/* Add new block */}
      <div style={{
        background: "#fff",
        border: "1px solid #e8e5f0",
        borderRadius: 12,
        padding: "18px 20px",
        marginBottom: 20,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1520", marginBottom: 14 }}>
          Dodaj blokadu
        </div>

        {/* Quick presets */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: "#7c6880", marginBottom: 6 }}>Brzi odabir</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => handlePreset(p)}
                style={{
                  padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  border: "1.5px solid #e8e5f0", background: "#f7f5ff",
                  color: "#5b50d6", cursor: "pointer",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date range */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: "#7c6880", marginBottom: 6 }}>Period</div>
          <RangePicker
            value={dateRange as [Dayjs, Dayjs]}
            onChange={(v) => setDateRange(v ? [v[0], v[1]] : [null, null])}
            format="DD.MM.YYYY"
            style={{ width: "100%" }}
            disabledDate={(d) => d.isBefore(dayjs().startOf("day"))}
          />
        </div>

        {/* Reason */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#7c6880", marginBottom: 6 }}>Razlog (opcionalno)</div>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="npr. Privatno korišćenje, renovacija..."
          />
        </div>

        <button
          type="button"
          onClick={handleAdd}
          disabled={saving || !dateRange[0] || !dateRange[1]}
          style={{
            padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: "#e53e3e", color: "#fff", border: "none", cursor: "pointer",
            opacity: saving || !dateRange[0] || !dateRange[1] ? 0.6 : 1,
          }}
        >
          Blokiraj period
        </button>
      </div>

      {/* Existing blocks */}
      {apt && (apt.blockedPeriods || []).length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#7c6880", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Aktivne blokade — {apt.name}
          </div>
          {[...(apt.blockedPeriods || [])].reverse().map((bp) => {
            const isPast = dayjs(bp.to).isBefore(dayjs().startOf("day"));
            return (
              <div
                key={bp._id}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 16px", marginBottom: 8,
                  background: isPast ? "#fafafa" : "#fff5f5",
                  border: `1.5px solid ${isPast ? "#e8e5f0" : "#feb2b2"}`,
                  borderRadius: 10,
                  opacity: isPast ? 0.6 : 1,
                }}
              >
                <div style={{ fontSize: 18 }}>🔒</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1520" }}>
                    {fmtDate(bp.from)} – {fmtDate(bp.to)}
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: "#9893a8" }}>
                      {nightCount(bp.from, bp.to)} {nightCount(bp.from, bp.to) === 1 ? "dan" : "dana"}
                    </span>
                  </div>
                  {bp.reason && (
                    <div style={{ fontSize: 11, color: "#7c6880", marginTop: 2 }}>{bp.reason}</div>
                  )}
                </div>
                {isPast && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#9893a8", textTransform: "uppercase" }}>Prošlo</span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(bp._id!)}
                  disabled={saving}
                  style={{
                    width: 28, height: 28, borderRadius: 6, border: "1px solid #fca5a5",
                    background: "#fff", color: "#dc2626", fontSize: 14,
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {apt && (apt.blockedPeriods || []).length === 0 && (
        <div style={{
          textAlign: "center", padding: "32px 0",
          color: "#9893a8", fontSize: 13,
          border: "1.5px dashed #e8e5f0", borderRadius: 12,
        }}>
          Nema blokiranih perioda za {apt.name}
        </div>
      )}
    </div>
  );
};

export default BlockedDatesTab;
